import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { StockDetailService } from '../../../../Services/Stock-Details/stock-detail.service';
import { HttpParams } from '@angular/common/http';
import { NgFor, NgIf, CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../../environments/environment';
import { NotificationService } from '../../../../Services/notification.service';

// Export Libraries
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';

@Component({
  selector: 'app-retail-stock',
  standalone: true,
  imports: [NgFor, NgIf, FormsModule, CommonModule],
  providers: [DecimalPipe],
  templateUrl: './retail-stock.component.html',
  styleUrl: './retail-stock.component.css'
})
export class RetailStockComponent implements OnInit, OnChanges {
  @Input() itemCode: string = '';
  
  retailList: any[] = [];
  filteredList: any[] = [];
  paginatedList: any[] = [];

  // Table Controls
  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 0;
  globalSearchText: string = '';
  sortKey: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  startDate: string = '';
  endDate: string = '';
  
  // Define columns matching your console log
  columns = [
    { key: 'std_image', label: 'Image', selected: true },
    { key: 'std_barcode', label: 'Barcode', selected: true },
    { key: 'std_item_code', label: 'Parent Code', selected: true },
    { key: 'category.name', label: 'Category', selected: true },
    { key: 'std_item_name', label: 'Product Name', selected: true },
    { key: 'std_per_gm_cost_without_packaging', label: 'W/O Packaging(Cost/GM)', selected: true },
    { key: 'std_per_gm_cost_with_packaging', label: 'With Packaging(Cost/GM)', selected: true },
    { key: 'std_stone_type', label: 'Stone Type', selected: true },
    { key: 'std_item_size', label: 'Size', selected: true },
    { key: 'std_net_weight', label: 'Weight', selected: true },
    { key: 'std_rate_list_code', label: 'Range Code', selected: true },
    { key: 'std_supplier_name', label: 'Supplier' }
  ];

  constructor(private stockDetailsService: StockDetailService, private notificationService: NotificationService) {}

  ngOnInit(): void {
    if (this.itemCode) this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemCode'] && !changes['itemCode'].firstChange) {
      this.loadData();
    }
  }

  loadData() {
    let params = new HttpParams().set('std_item_code', this.itemCode);
    this.stockDetailsService.getStockDetails(params).subscribe((res: any) => {
      this.retailList = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      this.filterTable();
    });
  }

  filterTable() {

  let filtered = [...this.retailList];

  /* ================= DATE FILTER ================= */

if (this.startDate || this.endDate) {

  console.log('----- DATE FILTER START -----');
  console.log('START DATE:', this.startDate);
  console.log('END DATE:', this.endDate);

  filtered = filtered.filter(item => {

    const itemDateRaw = item['std_entry_date']; // ✅ FIXED FIELD

    console.log('RAW ITEM DATE:', itemDateRaw);

    if (!itemDateRaw) {
      console.warn('❌ std_entry_date missing');
      return false;
    }

    // Convert "2026-02-14 10:58:36" → "2026-02-14"
    const itemDate = itemDateRaw.split(' ')[0];

    console.log('NORMALIZED DATE:', itemDate);

    if (this.startDate && itemDate < this.startDate) {
      console.log('❌ Before start date');
      return false;
    }

    if (this.endDate && itemDate > this.endDate) {
      console.log('❌ After end date');
      return false;
    }

    console.log('✅ Passed');
    return true;
  });

  console.log('TOTAL AFTER DATE FILTER:', filtered.length);
  console.log('----- DATE FILTER END -----');
}

  /* ================= GLOBAL SEARCH ================= */

  if (this.globalSearchText) {

    const search = this.globalSearchText.toLowerCase().trim();

    filtered = filtered.filter(item =>
      Object.values(item).some(val =>
        String(val ?? '')
          .toLowerCase()
          .includes(search)
      )
    );
  }

  /* ================= SORT ================= */

  if (this.sortKey) {

    filtered.sort((a, b) => {

      const valA = a[this.sortKey] ?? '';
      const valB = b[this.sortKey] ?? '';

      return this.sortDirection === 'asc'
        ? valA > valB ? 1 : valA < valB ? -1 : 0
        : valA < valB ? 1 : valA > valB ? -1 : 0;
    });
  }

  /* ================= SAVE FILTERED ================= */

  this.filteredList = filtered;

  /* ================= PAGINATION ================= */

  this.totalPages = Math.ceil(this.filteredList.length / this.itemsPerPage);

  if (this.currentPage > this.totalPages && this.totalPages > 0) {
    this.currentPage = this.totalPages;
  }

  this.updatePagination();
}


  updatePagination() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    this.paginatedList = this.filteredList.slice(start, start + this.itemsPerPage);
  }

  sortBy(key: string) {
    this.sortDirection = (this.sortKey === key && this.sortDirection === 'asc') ? 'desc' : 'asc';
    this.sortKey = key;
    this.filterTable();
  }
  /* ============================================================
   IMAGE PROXY HELPERS
============================================================ */

proxyImageUrl(url: string): string {
  if (!url) return url;
  if (url.includes('/api/image-proxy/')) return url;

  const match = url.match(/\/storage\/(.+)$/);
  if (!match) return url;

  const backendBase = environment.apiBaseUrl.replace(/\/api\/$/, '');
  return `${backendBase}/api/image-proxy/${match[1]}`;
}

/* ============================================================
   LOAD IMAGE AS BASE64
============================================================ */

async loadImageBase64(url: string): Promise<string | null> {
  try {
    const proxiedUrl = this.proxyImageUrl(url);
    const res = await fetch(proxiedUrl);
    if (!res.ok) throw new Error(res.statusText);

    const blob = await res.blob();

    return await new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });

  } catch (err) {
    console.error('Image load FAILED:', url, err);
    return null;
  }
}
async prepareRetailImages(): Promise<void> {

  for (const row of this.filteredList as any[]) {

    if (!row.std_image) continue;

    row.__img = await this.loadImageBase64(row.std_image1);
  }
}
getDisplayValue(value: any): string {

  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  return String(value);
}


  // --- DOWNLOAD FUNCTIONS --- 

  downloadPDF() {
  this.downloadPDFAsync();
}

async downloadPDFAsync(): Promise<void> {

  await this.prepareRetailImages();

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a3', // 🔥 same as stock list
  });

  const selectedColumns = this.columns.filter(col => col.selected);

  if (!selectedColumns.length) {
    this.notificationService.showError(
      'Please select at least one column',
      'Error'
    );
    return;
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  /* ---------- HEADER ---------- */

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(
    `Retail Stock Report - ${this.itemCode}`,
    pageWidth / 2,
    16,
    { align: 'center' }
  );

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Generated on: ${new Date().toLocaleString()}`,
    pageWidth - 15,
    10,
    { align: 'right' }
  );

  /* ---------- TABLE ---------- */

  const headers = [
    { header: 'No.', dataKey: 'no' },
    ...selectedColumns.map(col => ({
      header: col.label,
      dataKey: col.key,
    })),
  ];

 const sourceData = this.getExportSourceData();

const body = sourceData.map((item, index) => {

    const row: any = { no: index + 1 };

    selectedColumns.forEach(col => {

      const value = item;

      // 🔥 IMAGE COLUMN
      if (col.key === 'std_image') {
        row[col.key] = '';
        row.__img = item.__img;
      }

      // 🔥 NESTED KEY (category.name)
      else if (col.key.includes('.')) {

        const keys = col.key.split('.');
        let nestedValue: any = item;

        keys.forEach(k => {
          nestedValue = nestedValue?.[k];
        });

        row[col.key] = nestedValue ?? '';
      }

      // 🔥 NORMAL FIELD
      else {
        row[col.key] = this.getDisplayValue(item[col.key]);
      }

    });

    return row;
  });

  autoTable(doc, {
  columns: headers,
  body,

  startY: 24,

  margin: { left: 6, right: 6 },

  theme: 'grid',

  horizontalPageBreak: false,
  rowPageBreak: 'auto',

  // ✅ FORCE TABLE TO FIT PAGE WIDTH
  tableWidth: pageWidth - 12,

  styles: {
    fontSize: 5.8,          // 🔥 slightly smaller to fit all columns
    cellPadding: 1.2,
    overflow: 'linebreak',
    valign: 'middle',
  },

  headStyles: {
    fillColor: [193, 151, 37],
    textColor: 255,
    halign: 'center',
    fontSize: 6.5,
    fontStyle: 'bold',
  },

  columnStyles: {
    no: { cellWidth: 10, halign: 'center' },
    std_image: { cellWidth: 24 },
  },

  alternateRowStyles: {
    fillColor: [245, 245, 245],
  },

  didParseCell: (data: any) => {
    if (data.section !== 'body') return;

    if (data.column.dataKey === 'std_image') {
      data.cell.styles.minCellHeight = 30;
      data.cell.styles.halign = 'center';
      data.cell.styles.valign = 'middle';
    }
  },

  didDrawCell: (data: any) => {
    if (data.column.dataKey !== 'std_image') return;

    const rowData: any = data.row.raw;
    const img = rowData?.__img;

    if (!img) return;

    try {
      const imgW = 22;
      const imgH = 22;

      const x = data.cell.x + (data.cell.width - imgW) / 2;
      const y = data.cell.y + (data.cell.height - imgH) / 2;

      doc.addImage(
        img,
        'JPEG',
        x,
        y,
        imgW,
        imgH,
        undefined,
        'FAST'
      );
    } catch (err) {
      console.error('addImage failed:', err);
    }
  },

  didDrawPage: () => {
    doc.setFontSize(8);
    doc.text(
      `Page ${(doc as any).getNumberOfPages()}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  },
});

  doc.save(`Retail_${this.itemCode}.pdf`);
}



 downloadExcel(): void {

  const selectedColumns = this.columns.filter(col => col.selected);

  if (!this.paginatedList || this.paginatedList.length === 0) {
    return;
  }

  const numericKeys = [
    'std_per_gm_cost_without_packaging',
    'std_per_gm_cost_with_packaging',
    'std_net_weight'
  ];

  const sourceData = this.getExportSourceData();

const data = sourceData.map((item, index) => {

    const row: any = { 'No.': index + 1 };

    selectedColumns.forEach(col => {

      let value: any = '';

      // ✅ Handle nested keys (category.name)
      if (col.key.includes('.')) {
        const keys = col.key.split('.');
        value = keys.reduce((obj, key) => obj?.[key], item) ?? '';
      }
      else {
        value = item[col.key];
      }

      // 🖼 Image column → show URL instead of skipping
      if (col.key === 'std_image') {
        value = item.std_image ?? '';
      }

      // ✅ Force numeric
      if (numericKeys.includes(col.key)) {
        value =
          value !== null &&
          value !== undefined &&
          value !== ''
            ? Number(value)
            : 0;
      }

      row[col.label] = value ?? '';
    });

    return row;
  });

  const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);

  // ✅ Force numeric cell type
  Object.keys(worksheet).forEach(cell => {
    if (cell.startsWith('!')) return;

    const c = worksheet[cell];
    if (typeof c.v === 'number') {
      c.t = 'n';
    }
  });

  const workbook: XLSX.WorkBook = {
    Sheets: { 'Retail List': worksheet },
    SheetNames: ['Retail List']
  };

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });

  FileSaver.saveAs(
    new Blob([excelBuffer], {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    `Retail_${this.itemCode}.xlsx`
  );
}


  downloadCSV() {
    let csv = 'No.,Barcode,Model,Item,Qty,Weight,Status\n';
    const data = this.getExportSourceData();
    data.forEach((item, index) => {
      csv += `${index + 1},${item.std_barcode},${item.std_model_no},${item.std_item_name},${item.std_quantity},${item.std_net_weight},${item.std_current_status}\n`;
    });
    FileSaver.saveAs(new Blob([csv], { type: 'text/csv' }), `Retail_${this.itemCode}.csv`);
  }

  printTable() {
  this.printTableAsync();
}

async printTableAsync() {

  // 🔥 If you preload images like stock
  if (this.prepareRetailImages) {
    await this.prepareRetailImages();
  }

  // 🔥 Use full list (NOT paginated)
 const data = this.getExportSourceData();

  let printContent = `
  <html>
  <head>
    <title>Retail List</title>

    <style>

      body {
        font-family: Arial, sans-serif;
      }

      h3 {
        text-align: center;
        margin-bottom: 10px;
      }

      /* ===== TABLE GRID ===== */

      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 11px;
      }

      th, td {
        border-right: 2px solid #000;
        border-bottom: 2px solid #000;
        padding: 6px;
        text-align: center;
        vertical-align: middle;
        white-space: nowrap;
      }

      th:first-child,
      td:first-child {
        border-left: 2px solid #000;
      }

      thead th {
        border-top: 2px solid #000;
        background-color: #f2f2f2;
        font-weight: 600;
      }

      tbody tr:last-child td {
        border-bottom: 2px solid #000;
      }

      img {
        max-width: 60px;
        max-height: 60px;
        object-fit: contain;
        display: block;
        margin: auto;
      }

      @media print {

        @page {
          size: auto;
          margin: 8mm;
        }

        body {
          margin: 0;
        }

        thead {
          display: table-header-group;
        }

        tr {
          page-break-inside: avoid;
        }

      }

    </style>

  </head>
  <body>

    <h3>Retail List</h3>

    <table>
      <thead>
        <tr>
          <th>No.</th>
  `;

  // 🔥 HEADERS
  this.columns
    .filter(col => col.selected)
    .forEach(col => {
      printContent += `<th>${col.label}</th>`;
    });

  printContent += `
        </tr>
      </thead>
      <tbody>
  `;

  // 🔥 ROWS
  data.forEach((item: any, index: number) => {

    printContent += `
      <tr>
        <td>${index + 1}</td>
    `;

    this.columns
      .filter(col => col.selected)
      .forEach(col => {

        let value: any = '';

        // ✅ Handle nested keys (example: category.name)
        if (col.key.includes('.')) {
          const keys = col.key.split('.');
          value = keys.reduce((obj: any, key: string) => obj?.[key], item);
        }
        else {
          value = item[col.key];
        }

        // 🖼 IMAGE COLUMN
        if (col.key === 'std_image') {

          const img = item.__img || item.std_image;

          printContent += `
            <td>
              ${img ? `<img src="${img}" />` : ''}
            </td>
          `;
        }

        // ✅ Object relations
        else if (value && typeof value === 'object') {

          const display =
            value.name ||
            value.category_name ||
            value.title ||
            value.label ||
            value.type ||
            '';

          printContent += `<td>${display}</td>`;
        }

        // ✅ Normal value
        else {

          printContent += `<td>${value ?? ''}</td>`;
        }

      });

    printContent += `</tr>`;
  });

  printContent += `
      </tbody>
    </table>

  </body>
  </html>
  `;

  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }
}


  goToPage(p: number) {
    this.currentPage = p;
    this.updatePagination();
  }

  get pageNumbers() {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }
  private getExportSourceData(): any[] {

  const hasFilter =
    this.startDate ||
    this.endDate ||
    this.globalSearchText;

  return hasFilter
    ? this.filteredList
    : this.retailList;
}

}